import { Info } from '@mui/icons-material';
import { IconButton, ImageListItem, ImageListItemBar } from '@mui/material';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { EAttributeType } from '../../@types/AttributeTypes';
import { ETextureOrigin, IProductTexture, ProductImageListItemProps } from '../../@types/ContextProviderRelated';
import { EInteractionMode } from '../../@types/Enums';
import { EAssetType, IFilesApiColor, TTexture } from '../../@types/FilesAPIModels';
import AttributeModel from '../../models/standard/AttributeModel';
import { useBuilding } from '../../provider/BuildingProvider/BuildingProvider';
import { useToolbarContext } from '../../provider/ToolbarProvider';
import { getProductImageListItemBarStyles, getProductImageListItemIconButtonStyles, getProductImageListItemStyles } from '../../utils/imgUtils';
import { convertColorAssetToTTexture, convertMaterialToTTexture, loadProductMaterialTexture } from '../../utils/textureLoaderUtility';
import AssetSelectionModal from '../modals/AssetSelectionModal';
import LazyImage from './LazyImage';
import RoomComponentTooltipContainer from './RoomComponentTooltipContainer';
import Tooltip from './Tooltip';

const ProductTextureImageList = ({ product, index }: ProductImageListItemProps): JSX.Element => {
  // Destructure selectedObject along with other functions.
  const { getSelectedModel: getSelectedObject, setSelectedTexture, applyTextureToStoredObject } = useBuilding();
  const { setInteractionMode } = useToolbarContext();

  const [assetSelectionOpen, setAssetSelectionOpen] = useState(false);
  const [availableAssets, setAvailableAssets] = useState<TTexture[]>([]);
  const intl = useIntl();

  const handleClick = async () => {
    let assets: TTexture[] = [];
    // Gather material assets.
    const materialAssets = product.assets.filter((a) => a.assetType === EAssetType.MATERIAL);
    if (materialAssets.length > 0) {
      const materialTextures = materialAssets.map(convertMaterialToTTexture);
      assets = assets.concat(materialTextures);
    }
    // Gather color assets.
    const colorAssets = product.assets.filter((a) => a.assetType === EAssetType.COLOR).map(convertColorAssetToTTexture);
    if (colorAssets.length > 0) {
      assets = assets.concat(colorAssets);
    }

    if (assets.length > 1) {
      setAvailableAssets(assets);
      setAssetSelectionOpen(true);
    } else if (assets.length === 1) {
      await applyAsset(assets[0]);
    } else {
      console.warn('No assets available for product');
    }
  };

  const applyAsset = async (asset: TTexture) => {
    let textureInfo = null;
    
    // Updated Attribute Mapping Logic
    const attributesForTexture = (product.attributes || [])
      .filter(Boolean)
      .map((attr) => {
        return new AttributeModel(
          Number(attr.id),
          attr.key || '',
          attr.value,
          null, 
          attr.type as EAttributeType,
          attr.unit || null,
          false, 
          true,  
          attr.ownerId,
          attr.userId,
          attr.publicFlag,
          attr.localization,
          attr.defaults,
          attr.validation,
          attr.unitFamily
        );
      });

    if (asset.assetType === EAssetType.MATERIAL) {
      try {
        const texture = await loadProductMaterialTexture(product.id, asset.id);
        textureInfo = {
          textureOrigin: ETextureOrigin.PRODUCT,
          id: product.id,
          assetId: asset.id,
          name: asset.name,
          type: EAssetType.MATERIAL,
          material: texture,
          color: undefined,
          rotation: 0,
          offset: 0,
          objects: product.objects,
          attributes: attributesForTexture,
          country: product.country
        } as IProductTexture;
      } catch (error) {
        console.error('Error loading material texture:', error);
        return;
      }
    } else if (asset.assetType === EAssetType.COLOR) {
      textureInfo = {
        textureOrigin: ETextureOrigin.PRODUCT,
        id: product.id,
        assetId: asset.id,
        name: asset.name,
        type: EAssetType.COLOR,
        material: null,
        color: (asset as IFilesApiColor).hex,
        rotation: 0,
        offset: 0,
        objects: product.objects,
        attributes: attributesForTexture,
        country: product.country 
      } as IProductTexture;
    }

    if (!textureInfo) return;

    // Determine immediate mode based on whether a selected object exists.
    if (getSelectedObject()) {
      setSelectedTexture(textureInfo, true);
      applyTextureToStoredObject();
      setInteractionMode(EInteractionMode.NONE);
    } else {
      // Otherwise, set the texture for later application.
      setSelectedTexture(textureInfo);
      setInteractionMode(EInteractionMode.SET_TEXTURE);
    }
    setAssetSelectionOpen(false);
  };

  return (
    <>
      <ImageListItem cols={1} rows={1} key={`${product.name}-${index}`} sx={getProductImageListItemStyles()} onClick={handleClick}>
        <LazyImage item={product} />
        <ImageListItemBar
          key={`${product.name}-imgBar`}
          className={'imgBar'}
          title={product.name}
          subtitle={product.manufacturer?.name || ''}
          actionIcon={
            <Tooltip
              PopperProps={{
                modifiers: [
                  { name: 'preventOverflow', options: { boundary: 'window' } },
                  { name: 'offset', options: { offset: [8, 0] } },
                ],
              }}
              title={<RoomComponentTooltipContainer roomComponent={product} isTexture={true} />}
              placement="top-start"
            >
              <IconButton className={'iconBtn'} sx={getProductImageListItemIconButtonStyles()} aria-label={`info about ${product.name}`}>
                <Info />
              </IconButton>
            </Tooltip>
          }
          sx={getProductImageListItemBarStyles()}
        />
      </ImageListItem>
      <AssetSelectionModal
        open={assetSelectionOpen}
        title={intl.formatMessage({ id: 'ProductSelection.selectSurfaceTitle' }, { productName: product.name })}
        assets={availableAssets}
        onClose={() => {
          setAssetSelectionOpen(false);
          setInteractionMode(EInteractionMode.NONE);
        }}
        onSelect={applyAsset}
        origin={ETextureOrigin.PRODUCT}
        productId={product.id}
      />
    </>
  );
};

export default ProductTextureImageList;